<template>
  <span
    v-if="lines && lines.length > 0"
    ref="spanElement"
    class="lf-animated-headline__content-rotation"
    :class="{ 'lf-animated-headline__content-rotation--full-width': isFullWidth }"
    :style="inlineStyles"
  >
    <LfAnimatedHeadlineRotationLine
      v-for="(line, index) in lines"
      v-show="index === rotateIndex || !domLoaded"
      :key="index"
      :line="line"
      :animation="animation"
      :style="gradientColor"
      @setBounds="setBounds"
    />
  </span>
  <h8 ref="longestStringRef" class="lf-animation-headline__hide">
    <ApplyReplacementTags :html="longestString" />
  </h8>
</template>

<script lang="ts">
import type { PropType, CSSProperties } from 'vue';
import { defineComponent, onMounted, ref, computed, onBeforeUnmount } from 'vue';
import LfAnimatedHeadlineRotationLine from '@/src/components/addons/lf-animated-headline/Rotation/LfAnimatedHeadlineRotationLine.vue';
import type { AnimatedHeadlineAnimationType } from '@/src/typings/enums/enums';
import ApplyReplacementTags from '@/src/components/ApplyReplacementTags.vue';

export default defineComponent({
  name: 'LfAnimatedHeadlineRotation',
  components: {
    ApplyReplacementTags,
    LfAnimatedHeadlineRotationLine
  },
  props: {
    lines: {
      type: Array as PropType<Array<string>>
    },
    animation: {
      type: String as PropType<AnimatedHeadlineAnimationType>
    },
    rotateTextColor: {
      type: String
    },
    gradientColor: {
      type: Object as PropType<CSSProperties>
    },
    isFullWidth: {
      type: Boolean
    }
  },
  setup(props) {
    const spanElement = ref<HTMLElement>();
    const longestStringRef = ref<HTMLElement | null>(null);
    const rotateIndex = ref(0);
    const width = ref(0);
    const height = ref(0);
    const lineHeights = ref<number[]>([]);
    const lineWidths = ref<number[]>([]);
    const domLoaded = ref(false);
    let interval: number | undefined;

    const longestString = computed(() => {
      return (props.lines as string[]).reduce(function (a, b) {
        return a.length > b.length ? a : b;
      }, '');
    });

    const inlineStyles = computed<CSSProperties>(() => {
      return {
        ...(props.rotateTextColor && { color: props.rotateTextColor }),
        ...props.gradientColor,
        width: `${width.value}px`,
        height: `${height.value}px`
      };
    });

    const textRotate = () => {
      rotateIndex.value += 1;
      if (rotateIndex.value >= (props.lines as string[]).length) {
        rotateIndex.value = 0;
      }
    };

    const setBounds = (bounds: DOMRect) => {
      lineHeights.value.push(bounds.height);
      lineWidths.value.push(bounds.width);
      // Get and set the highest number from array
      const highestHeight = Math.max(...lineHeights.value);
      const highestWidth = Math.max(...lineWidths.value);

      height.value = Math.round(highestHeight);
      width.value = Math.round(highestWidth);
    };

    onMounted(() => {
      interval = window.setInterval(() => {
        textRotate();
      }, 2500);

      if (spanElement.value) {
        width.value = spanElement.value?.parentElement?.parentElement?.clientWidth || 0;
      }

      domLoaded.value = true;
    });

    onBeforeUnmount(() => {
      if (interval !== undefined) {
        clearInterval(interval);
      }
    });

    return {
      setBounds,
      domLoaded,
      spanElement,
      inlineStyles,
      rotateIndex,
      longestString,
      longestStringRef
    };
  }
});
</script>

<style lang="scss">
.lf-animation-headline__hide {
  position: absolute;
  visibility: hidden;
  opacity: 0;
  left: -9999em;
  top: -9999em;
}
</style>
